import type {
  BYOKKeyKind,
  BYOKTier,
  SelectableHealth,
  UnavailableHealth,
} from '@developerz/domain';
import { For, type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import { AGENT_NAME_KEY } from '../lib/agents';
import type { PickerRow } from '../lib/model-picker';
import { clockOrDay } from '../lib/time';
import { ProviderMark } from './KeyCard';

// The body of one model option in the `ModelPicker` (#5210, epic #5200) — Card
// anatomy's row: the provider mark, the display name plus the raw id in mono, the
// tier and billing badges, a health dot with "on n keys", the amber reset chip, and
// "Reserved for …". `ModelPickerRow` owns the option element around it.
//
// Every label map is STATIC and total over its domain vocabulary, so a tier or a
// health state the domain grows is a compile error here rather than a `⟦key⟧`.
//
// THE REASONING BADGE reads the offer's DETECTED capability, and it reads it on
// EVERY offer that carries one: an unavailable offer is shown for awareness, not
// picked, and the domain says its badge reads the same way
// (`ModelOffer.reasoning` / `UnavailableOffer.reasoning`, `@developerz/domain`).
// The capability itself arrives on the key's catalogue row
// (`lib/keys-types.ts`, `KeyModelRow.reasoning`) and survives the wire into the
// domain's offer — nothing else feeds this badge.

const TIER_LABEL: Readonly<Record<BYOKTier, string>> = {
  heavy: 'agents.picker.tier.heavy',
  smart: 'agents.picker.tier.smart',
  general: 'agents.picker.tier.general',
  fast: 'agents.picker.tier.fast',
};

/** The key card's own billing labels, so the two screens name a key's kind alike. */
const KIND_LABEL: Readonly<Record<BYOKKeyKind, string>> = {
  metered: 'account_keys.kind_metered',
  subscription: 'account_keys.kind_subscription',
};

const HEALTH_LABEL: Readonly<Record<SelectableHealth | UnavailableHealth, string>> = {
  valid: 'agents.picker.health.valid',
  rate_limited: 'agents.picker.health.rate_limited',
  credits_exhausted: 'agents.picker.health.credits_exhausted',
  unverified: 'agents.picker.health.unverified',
  invalid: 'agents.picker.health.invalid',
};

const onKeys = (n: number): string => tPlural('agents.picker.on_keys', n);

export function ModelPickerOffer(props: {
  readonly row: Extract<PickerRow, { kind: 'model' | 'unavailable' }>;
}): JSX.Element {
  const offer = () => props.row.offer;
  const model = () => (props.row.kind === 'model' ? props.row.offer : null);
  const health = (): SelectableHealth | UnavailableHealth =>
    props.row.kind === 'model' ? props.row.offer.bestHealth : props.row.offer.health;
  return (
    <>
      <ProviderMark provider={offer().provider} />
      <span class="model-picker__label">
        <span class="model-picker__name">{offer().displayName}</span>
        <code class="model-picker__id">{`${offer().provider}/${offer().model}`}</code>
      </span>
      <span class="model-picker__facts">
        <Show when={model()?.tier}>
          {(tier) => <span class="badge model-picker__tier">{t(TIER_LABEL[tier()])}</span>}
        </Show>
        <Show when={offer().reasoning?.supported === true}>
          <span class="badge model-picker__reasoning">{t('agents.picker.reasoning')}</span>
        </Show>
        <For each={model()?.kinds ?? []}>
          {(kind) => <span class="badge model-picker__kind">{t(KIND_LABEL[kind])}</span>}
        </For>
        <span class="model-picker__health" data-health={health()}>
          <span class="model-picker__dot" aria-hidden="true" />
          {t(HEALTH_LABEL[health()])} · {onKeys(offer().keyCount)}
        </span>
        <Show when={model()?.soonestResetIso}>
          {(iso) => (
            <span class="model-picker__reset">
              {t('agents.picker.reset', { when: clockOrDay(iso()) })}
            </span>
          )}
        </Show>
        <Show when={offer().reservedFor}>
          {(agent) => (
            <span class="model-picker__reserved">
              {t('agents.picker.reserved', { agent: t(AGENT_NAME_KEY[agent()]) })}
            </span>
          )}
        </Show>
      </span>
    </>
  );
}
